<!--
  Employee Injury Report Form
  
  Step-by-step incident reporting with:
  - 5 steps: Employee Info, Job Info, Incident Info, Injury Info, Attachments & Signature
  - Auto-populated employee information from current user
  - Medical treatment contact information when required
  - Phone number validation with custom format enforcement
  - Signature and upload requirements on final step
-->

<!-- Use the form layout component to handle the wizard UI -->
<app-form-layout
  [title]="formTitle"
  [form]="form"
  [icon]="formIcon"
  [disabled]="submitting"
  
  [onSubmit]="onSubmit.bind(this)"
  [steps]="steps"
  [currentStep]="currentStep()"
  [canNavigateToStep]="canNavigateToStep.bind(this)"
  [completedSteps]="completedSteps()"
  [onNextStep]="onNextStep.bind(this)"
  [onPreviousStep]="onPreviousStep.bind(this)"
  [getProgressPercentage]="getProgressPercentage.bind(this)">

  <div *ngIf="form" formContent [formGroup]="form" class="form-content">
    
    <!-- Employee Information Step -->
    <div *ngIf="currentStep() === steps[0].id" class="step-content">
      <ion-list>
        <ion-item-divider class="form-step-title">
          <ion-label>
            <h1>{{steps[0].title}}</h1>
            <p>{{steps[0].description}}</p>
          </ion-label>
        </ion-item-divider>

        <ion-grid>
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="employeeInfoFormGroup"
                [control]="employeeInfoFormGroup.get('employeeFirstName')!"
                [label]="fieldMappings['employeeFirstName']"
                [requiredField]="requiredFields['employeeFirstName']"
                placeholder="Enter first name">
              </app-text-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="employeeInfoFormGroup"
                [control]="employeeInfoFormGroup.get('employeeLastName')!"
                [label]="fieldMappings['employeeLastName']"
                [requiredField]="requiredFields['employeeLastName']"
                placeholder="Enter last name">
              </app-text-input>
            </ion-col>
          </ion-row>

          <ion-row>
            <ion-col>
              <app-text-input
                [templateFormGroup]="employeeInfoFormGroup"
                [control]="employeeInfoFormGroup.get('streetAddress')!"
                [label]="fieldMappings['streetAddress']"
                [requiredField]="requiredFields['streetAddress']"
                placeholder="Enter street address">
              </app-text-input>
            </ion-col>
          </ion-row>

          <ion-row>
            <ion-col size="12" size-md="4">
              <app-text-input
                [templateFormGroup]="employeeInfoFormGroup"
                [control]="employeeInfoFormGroup.get('city')!"
                [label]="fieldMappings['city']"
                [requiredField]="requiredFields['city']"
                placeholder="Enter city">
              </app-text-input>
            </ion-col>
            <ion-col size="12" size-md="4">
              <app-text-input
                [templateFormGroup]="employeeInfoFormGroup"
                [control]="employeeInfoFormGroup.get('stateProvince')!"
                [label]="fieldMappings['stateProvince']"
                [requiredField]="requiredFields['stateProvince']"
                placeholder="Enter state/province">
              </app-text-input>
            </ion-col>
            <ion-col size="12" size-md="4">
              <app-text-input
                [templateFormGroup]="employeeInfoFormGroup"
                [control]="employeeInfoFormGroup.get('zipPostalCode')!"
                [label]="fieldMappings['zipPostalCode']"
                [requiredField]="requiredFields['zipPostalCode']"
                type="number"
                placeholder="Enter ZIP/postal code">
              </app-text-input>
            </ion-col>
          </ion-row>

          <ion-row>
            <ion-col>
              <app-text-input
                [templateFormGroup]="employeeInfoFormGroup"
                [control]="employeeInfoFormGroup.get('phone')!"
                [label]="fieldMappings['phone']"
                type="tel"
                [requiredField]="requiredFields['phone']"
                placeholder="(xxx) xxx-xxxx">
              </app-text-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-date-input
                [templateFormGroup]="employeeInfoFormGroup"
                [control]="employeeInfoFormGroup.get('dateOfHire')!"
                [label]="fieldMappings['dateOfHire']"
                [requiredField]="requiredFields['dateOfHire']">
              </app-date-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="employeeInfoFormGroup"
                [control]="employeeInfoFormGroup.get('craftAndPosition')!"
                [label]="fieldMappings['craftAndPosition']"
                [requiredField]="requiredFields['craftAndPosition']"
                placeholder="Enter craft and position">
              </app-text-input>
            </ion-col>
          </ion-row>
        </ion-grid>
      </ion-list>
    </div>

    <!-- Job Information Step -->
    <div *ngIf="currentStep() === steps[1].id" class="step-content">
      <ion-list>
        <ion-item-divider class="form-step-title">
          <ion-label>
            <h1>{{steps[1].title}}</h1>
            <p>{{steps[1].description}}</p>
          </ion-label>
        </ion-item-divider>

        <ion-grid>


          <ion-row>
            <ion-col >
              <app-text-input
                [templateFormGroup]="jobInfoFormGroup"
                [control]="jobInfoFormGroup.get('shiftWorked')!"
                [label]="fieldMappings['shiftWorked']"
                [requiredField]="requiredFields['shiftWorked']"
                placeholder="Enter shift worked">
              </app-text-input>
            </ion-col>

          </ion-row>

        

          <ion-row>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="jobInfoFormGroup"
                [control]="jobInfoFormGroup.get('hoursWorkedPerDay')!"
                [label]="fieldMappings['hoursWorkedPerDay']"
                [requiredField]="requiredFields['hoursWorkedPerDay']"
                type="number"
                placeholder="Enter hours worked per day">
              </app-text-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="jobInfoFormGroup"
                [control]="jobInfoFormGroup.get('hoursWorkedPerWeek')!"
                [label]="fieldMappings['hoursWorkedPerWeek']"
                [requiredField]="requiredFields['hoursWorkedPerWeek']"
                type="number"
                placeholder="Enter hours worked per week">
              </app-text-input>
            </ion-col>
          </ion-row>

          <ion-row>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="jobInfoFormGroup"
                [control]="jobInfoFormGroup.get('supervisorFirstName')!"
                [label]="fieldMappings['supervisorFirstName']"
                [requiredField]="requiredFields['supervisorFirstName']"
                placeholder="Enter supervisor's first name">
              </app-text-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="jobInfoFormGroup"
                [control]="jobInfoFormGroup.get('supervisorLastName')!"
                [label]="fieldMappings['supervisorLastName']"
                [requiredField]="requiredFields['supervisorLastName']"
                placeholder="Enter supervisor's last name">
              </app-text-input>
            </ion-col>
          </ion-row>
        </ion-grid>
      </ion-list>
    </div>

    <!-- Incident Information Step -->
    <div *ngIf="currentStep() === steps[2].id" class="step-content">
      <ion-list>
        <ion-item-divider class="form-step-title">
          <ion-label>
            <h1>{{steps[2].title}}</h1>
            <p>{{steps[2].description}}</p>
          </ion-label>
        </ion-item-divider>

        <ion-grid>
          <ion-row>
            <ion-col>
              <app-select-input
                [templateFormGroup]="incidentInfoFormGroup"
                [control]="incidentInfoFormGroup.get('typeOfIncident')!"
                [label]="fieldMappings['typeOfIncident']"
                [requiredField]="requiredFields['typeOfIncident']"
                [options]="['Injury/Illness', 'First-Aid', 'Traffic/Auto (Contact Fleet Manager)', 'Property Damage', 'Other']"
                [placeholder]="'Select an option'">
              </app-select-input>
            </ion-col>
          </ion-row>

          <ion-row>
            <ion-col size="12" size-md="6">
              <app-date-input
                [templateFormGroup]="incidentInfoFormGroup"
                [control]="incidentInfoFormGroup.get('dateOfIncident')!"
                [label]="fieldMappings['dateOfIncident']"
                [requiredField]="requiredFields['dateOfIncident']">
              </app-date-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="incidentInfoFormGroup"
                [control]="incidentInfoFormGroup.get('timeOfIncident')!"
                [label]="fieldMappings['timeOfIncident']"
                [requiredField]="requiredFields['timeOfIncident']"
                type="time"
                placeholder="Enter time of incident">
              </app-text-input>
            </ion-col>
          </ion-row>

          <ion-row>
            <ion-col size="12" size-md="6">
              <app-date-input
                [templateFormGroup]="incidentInfoFormGroup"
                [control]="incidentInfoFormGroup.get('dateReported')!"
                [label]="fieldMappings['dateReported']"
                [requiredField]="requiredFields['dateReported']">
              </app-date-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="incidentInfoFormGroup"
                [control]="incidentInfoFormGroup.get('timeReported')!"
                [label]="fieldMappings['timeReported']"
                [requiredField]="requiredFields['timeReported']"
                type="time"
                placeholder="Enter time reported">
              </app-text-input>
            </ion-col>
          </ion-row>

          <ion-row>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="incidentInfoFormGroup"
                [control]="incidentInfoFormGroup.get('incidentReportedToFirstName')!"
                [label]="fieldMappings['incidentReportedToFirstName']"
                [requiredField]="requiredFields['incidentReportedToFirstName']"
                placeholder="Enter first name">
              </app-text-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="incidentInfoFormGroup"
                [control]="incidentInfoFormGroup.get('incidentReportedToLastName')!"
                [label]="fieldMappings['incidentReportedToLastName']"
                [requiredField]="requiredFields['incidentReportedToLastName']"
                placeholder="Enter last name">
              </app-text-input>
            </ion-col>
          </ion-row>

          <ion-row>
            <ion-col>
              <app-text-input
                [templateFormGroup]="incidentInfoFormGroup"
                [control]="incidentInfoFormGroup.get('jobSiteOfInjury')!"
                [label]="fieldMappings['jobSiteOfInjury']"
                [requiredField]="requiredFields['jobSiteOfInjury']"
                placeholder="Enter job site of injury">
              </app-text-input>
            </ion-col>
          </ion-row>

          <ion-row>
            <ion-col>
              <app-text-input
                [templateFormGroup]="incidentInfoFormGroup"
                [control]="incidentInfoFormGroup.get('exactLocationOfInjury')!"
                [label]="fieldMappings['exactLocationOfInjury']"
                [requiredField]="requiredFields['exactLocationOfInjury']"
                placeholder="Enter exact location of injury">
              </app-text-input>
            </ion-col>
          </ion-row>

          <ion-row>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="incidentInfoFormGroup"
                [control]="incidentInfoFormGroup.get('witness1')!"
                [label]="fieldMappings['witness1']"
                [requiredField]="requiredFields['witness1']"  
                placeholder="Enter witness 1">
              </app-text-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              
              <app-text-input
                [templateFormGroup]="incidentInfoFormGroup"
                [control]="incidentInfoFormGroup.get('witness2')!"
                [label]="fieldMappings['witness2']"
                [requiredField]="requiredFields['witness2']"  
                placeholder="Enter witness 2">
              </app-text-input>
            </ion-col>
          </ion-row>

          <ion-row>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="incidentInfoFormGroup"
                [control]="incidentInfoFormGroup.get('equipment1')!"
                [label]="fieldMappings['equipment1']"
                [requiredField]="requiredFields['equipment1']"
                placeholder="Enter equipment 1">
              </app-text-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="incidentInfoFormGroup"
                [control]="incidentInfoFormGroup.get('equipment2')!"
                [label]="fieldMappings['equipment2']"
                [requiredField]="requiredFields['equipment2']"
                placeholder="Enter equipment 2">
              </app-text-input>
            </ion-col>
          </ion-row>

          <ion-row>
            <ion-col>
              <app-text-input
                [templateFormGroup]="incidentInfoFormGroup"
                [control]="incidentInfoFormGroup.get('anyDefects')!"
                [label]="fieldMappings['anyDefects']"
                [requiredField]="requiredFields['anyDefects']"
                placeholder="Enter any defects">
              </app-text-input>
            </ion-col>
          </ion-row>

          <ion-row>
            <ion-col>
              <app-radio-group-input
                [templateFormGroup]="incidentInfoFormGroup"
                [control]="incidentInfoFormGroup.get('coveredInPreTask')!"
                [label]="fieldMappings['coveredInPreTask']"
                [requiredField]="requiredFields['coveredInPreTask']"  
                [options]="['Yes', 'No']"
                [orientation]="'horizontal'">
              </app-radio-group-input>
            </ion-col>
          </ion-row>

          <ion-row>
            <ion-col>
              <app-textarea-input
                [templateFormGroup]="incidentInfoFormGroup"
                [control]="incidentInfoFormGroup.get('taskDescription')!"
                [label]="fieldMappings['taskDescription']"
                [requiredField]="requiredFields['taskDescription']"
                [rows]="4"
                placeholder="Describe in detail the task being performed at the time of the incident">
              </app-textarea-input>
            </ion-col>
          </ion-row>

          <ion-row>
            <ion-col>
              <app-textarea-input
                [templateFormGroup]="incidentInfoFormGroup"
                [control]="incidentInfoFormGroup.get('howInjuryOccurred')!"
                [label]="fieldMappings['howInjuryOccurred']"
                [requiredField]="requiredFields['howInjuryOccurred']"
                [rows]="4"
                placeholder="Describe how the injury occurred">
              </app-textarea-input>
            </ion-col>
          </ion-row>


          <ion-row>
            <ion-col>
              <app-textarea-input
                [templateFormGroup]="incidentInfoFormGroup"
                [control]="incidentInfoFormGroup.get('contributingFactors')!"
                [label]="fieldMappings['contributingFactors']"
                [requiredField]="requiredFields['contributingFactors']"
                [rows]="4"
                placeholder="Describe the contributing factors that led to the incident">
              </app-textarea-input>
            </ion-col>
          </ion-row>
        </ion-grid>
      </ion-list>
    </div>

    <!-- Injury Information Step -->
    <div *ngIf="currentStep() === steps[3].id" class="step-content">
      <ion-list>
        
        <ion-item-divider class="form-step-title">
          <ion-label>
              <h1>{{steps[3].title}}</h1>
            <p>{{steps[3].description}}</p>
          </ion-label>
        </ion-item-divider>

        <ion-grid>

          
          <ion-row>
            <ion-col>
              <app-radio-group-input
                [templateFormGroup]="injuryInfoFormGroup"
                [control]="injuryInfoFormGroup.get('injured')!"
                [label]="fieldMappings['injured']"
                [requiredField]="requiredFields['injured']"
                [options]="['Yes', 'No']"
                [orientation]="'horizontal'">
              </app-radio-group-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col>
              <app-text-input
                [templateFormGroup]="injuryInfoFormGroup"
                [control]="injuryInfoFormGroup.get('natureOfInjury')!"
                [label]="fieldMappings['natureOfInjury']"
                [requiredField]="requiredFields['natureOfInjury']"
                placeholder="Enter nature of injury">
              </app-text-input>
            </ion-col>
          </ion-row>

          <ion-row>
            <ion-col>
              <app-text-input
                [templateFormGroup]="injuryInfoFormGroup"
                [control]="injuryInfoFormGroup.get('bodyPartInjured')!"
                [label]="fieldMappings['bodyPartInjured']"
                [requiredField]="requiredFields['bodyPartInjured']"
                placeholder="Enter body part injured">
              </app-text-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col>
              <app-textarea-input
                [templateFormGroup]="injuryInfoFormGroup"
                [control]="injuryInfoFormGroup.get('injuryDescription')!"
                [label]="fieldMappings['injuryDescription']"
                [requiredField]="requiredFields['injuryDescription']"
                [rows]="4"
                placeholder="Describe the injury">
              </app-textarea-input>
            </ion-col>
          </ion-row>




          <ion-row>
            <ion-col>
              <app-radio-group-input
                [templateFormGroup]="injuryInfoFormGroup"
                [control]="injuryInfoFormGroup.get('seekMedicalTreatment')!"
                [label]="fieldMappings['seekMedicalTreatment']"
                [requiredField]="requiredFields['seekMedicalTreatment']"
                [options]="['Yes', 'No']"
                [orientation]="'horizontal'">
              </app-radio-group-input>
            </ion-col>
          </ion-row>

          <ion-row *ngIf="injuryInfoFormGroup.get('seekMedicalTreatment')?.value === 'Yes'">
            <ion-col>
              <ion-card color="warning" class="information-card">
                <ion-card-content>
                  <ion-text >
                    <h5><ion-icon name="alert-circle"></ion-icon>&nbsp;Contact the Safety Director - <strong>206.255.3388</strong></h5>
                  </ion-text>
                </ion-card-content>
              </ion-card>
            </ion-col>
          </ion-row>


          <ion-row>
            <ion-col>
              <app-radio-group-input
                [templateFormGroup]="injuryInfoFormGroup"
                [control]="injuryInfoFormGroup.get('previousSimilarCondition')!"
                [label]="fieldMappings['previousSimilarCondition']"
                [requiredField]="requiredFields['previousSimilarCondition']"
                [options]="['Yes', 'No']"
                [orientation]="'horizontal'">
              </app-radio-group-input>
            </ion-col>
          </ion-row>

          <ion-row>
            <ion-col>
              <app-textarea-input
                [templateFormGroup]="injuryInfoFormGroup"
                [control]="injuryInfoFormGroup.get('previousInjuryDescription')!"
                [label]="fieldMappings['previousInjuryDescription']"
                [requiredField]="requiredFields['previousInjuryDescription']"
                [rows]="4"
                placeholder="Describe previous injury">
              </app-textarea-input>
            </ion-col>
          </ion-row>

 

          <ion-row>
            <ion-col>
              <app-radio-group-input
                [templateFormGroup]="injuryInfoFormGroup"
                [control]="injuryInfoFormGroup.get('ppeRequired')!"
                [label]="fieldMappings['ppeRequired']"
                [requiredField]="requiredFields['ppeRequired']"
                [options]="['Yes', 'No']"
                [orientation]="'horizontal'">
              </app-radio-group-input>
            </ion-col>
          </ion-row>

          <ion-row>
            <ion-col>
              <app-radio-group-input
                [templateFormGroup]="injuryInfoFormGroup"
                [control]="injuryInfoFormGroup.get('ppeWorn')!"
                [label]="fieldMappings['ppeWorn']"
                [requiredField]="requiredFields['ppeWorn']"
                [options]="['Yes', 'No']"
                [orientation]="'horizontal'">
              </app-radio-group-input>
            </ion-col>
          </ion-row>

          <ion-row>
            <ion-col>
              <app-radio-group-input
                [templateFormGroup]="injuryInfoFormGroup"
                [control]="injuryInfoFormGroup.get('adequateLighting')!"
                [label]="fieldMappings['adequateLighting']"
                [requiredField]="requiredFields['adequateLighting']"
                [options]="['Yes', 'No']"
                [orientation]="'horizontal'">
              </app-radio-group-input>
            </ion-col>
          </ion-row>


          <ion-row>
            <ion-col>
              <app-radio-group-input
                [templateFormGroup]="injuryInfoFormGroup"
                [control]="injuryInfoFormGroup.get('bloodExposure')!"
                [label]="fieldMappings['bloodExposure']"
                [requiredField]="requiredFields['bloodExposure']"
                [options]="['Yes', 'No']"
                [orientation]="'horizontal'">
              </app-radio-group-input>
            </ion-col>
          </ion-row>



   
         
        </ion-grid>
      </ion-list>
    </div>

    <!-- Attachments & Signature Step -->
    <div *ngIf="currentStep() === steps[4].id" class="step-content">
      <ion-list>
        
        <ion-item-divider class="form-step-title">
          <ion-label>
            <h1>{{steps[4].title}}</h1>
            <p>{{steps[4].description}}</p>
          </ion-label>
        </ion-item-divider>

        <ion-grid>

          
          <ion-row>
            <ion-col>
              <app-radio-group-input
                [templateFormGroup]="additionalInfoFormGroup"
                [control]="additionalInfoFormGroup.get('propertyDamage')!"
                [label]="fieldMappings['propertyDamage']"
                [requiredField]="requiredFields['propertyDamage']"
                [options]="['Yes', 'No']"
                [orientation]="'horizontal'">
              </app-radio-group-input>
            </ion-col>
          </ion-row>

          <ion-row>
            <ion-col>
              <app-radio-group-input
                [templateFormGroup]="additionalInfoFormGroup"
                [control]="additionalInfoFormGroup.get('othersInvolved')!"
                [label]="fieldMappings['othersInvolved']"
                [requiredField]="requiredFields['othersInvolved']"
                [options]="['Yes', 'No']"
                [orientation]="'horizontal'">
              </app-radio-group-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col>
              <app-textarea-input
                [templateFormGroup]="additionalInfoFormGroup"
                [control]="additionalInfoFormGroup.get('preventionSuggestion')!"
                [label]="fieldMappings['preventionSuggestion']"
                [requiredField]="requiredFields['preventionSuggestion']"
                [rows]="4"
                placeholder="Suggest how this incident could have been avoided">
              </app-textarea-input>
            </ion-col>
          </ion-row>

          <ion-row>
            <ion-col>
              <app-textarea-input
                [templateFormGroup]="additionalInfoFormGroup"
                [control]="additionalInfoFormGroup.get('employeeStatement')!"
                [label]="fieldMappings['employeeStatement']"
                [requiredField]="requiredFields['employeeStatement']"
                [rows]="4"
                placeholder="Enter additional employee statement">
              </app-textarea-input>
            </ion-col>
          </ion-row>

          <app-attachment-upload-section *ngFor="let upload of formAttachmentRequirements.uploads"
          [uploadInfo]="upload">
        </app-attachment-upload-section>

        <ion-row *ngFor="let signature of formAttachmentRequirements.signatures">
          <app-signature-section [signatureInfo]="signature"></app-signature-section>
        </ion-row>
        <ion-row>
          <ion-card color="primary" class="information-card">
            <ion-card-header><ion-card-title><ion-icon name="finger-print-outline"></ion-icon>&nbsp;Declaration</ion-card-title></ion-card-header>
            <ion-card-content>
              <ion-text>
                <h4>The facts as I have stated them are true to the best of my knowledge. 
                  <br><br>In signing this form, I permit the release of all information related to this injury to my employer and his/her authorized representative. 
                  <br>
                  <br>A copy of this permission constitutes as an original.</h4>
              </ion-text>
            </ion-card-content>
          </ion-card>        </ion-row>
        <ion-row>
          <app-date-input
            [templateFormGroup]="additionalInfoFormGroup"
            [control]="additionalInfoFormGroup.get('signatureDate')!"
            [label]="fieldMappings['signatureDate']"
            [requiredField]="requiredFields['signatureDate']">
          </app-date-input>
        </ion-row>
        </ion-grid>

      
      </ion-list>
    </div>
  </div>
</app-form-layout> 